import { createClient } from "@supabase/supabase-js";
import { boundedNavigatorLock } from "./authLock";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const plplSupabaseUrl = process.env.REACT_APP_SUPABASE_URL2;
const plplSupabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY2;

// 싱글톤 패턴으로 클라이언트 인스턴스 관리
let supabaseInstance = null;
let plplSupabaseInstance = null;

// 메인 Supabase 클라이언트 (싱글톤, 브라우저에서는 window에 보존해 HMR 시 재생성 방지)
const getSupabaseClient = () => {
  if (typeof window !== 'undefined' && window.__SUPABASE_MAIN__) {
    return window.__SUPABASE_MAIN__;
  }
  if (!supabaseInstance) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: 'sb-main-auth-token',
        // 탭 간 인증 잠금에 5초 상한 — 얼어붙은 백그라운드 탭이 잠금을 쥐면 새 탭이 getSession() 에서
        // 영원히 기다리던 문제(2026-09-05 모바일 무한 로딩). 상세는 src/lib/authLock.js.
        lock: boundedNavigatorLock,
      },
    });
    if (typeof window !== 'undefined') {
      window.__SUPABASE_MAIN__ = supabaseInstance;
    }
  }
  return supabaseInstance;
};

// PLPL Supabase 클라이언트 (싱글톤, 브라우저에서는 window에 보존해 HMR 시 재생성 방지)
const getPlplSupabaseClient = () => {
  if (typeof window !== 'undefined' && window.__SUPABASE_PLPL__) {
    return window.__SUPABASE_PLPL__;
  }
  if (!plplSupabaseInstance && plplSupabaseUrl && plplSupabaseAnonKey) {
    plplSupabaseInstance = createClient(plplSupabaseUrl, plplSupabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: 'sb-plpl-auth-token',
        lock: boundedNavigatorLock,
      },
    });
    if (typeof window !== 'undefined') {
      window.__SUPABASE_PLPL__ = plplSupabaseInstance;
    }
  }
  return plplSupabaseInstance;
};

// 메인 Supabase 클라이언트 (모듈 로드 시 한 번만 생성)
export const supabase = getSupabaseClient();

// PLPL Supabase 클라이언트: 실제 사용 시에만 생성 (React Refresh 등이 export 검사할 때는 생성 안 함)
const PLPL_PROXY_SKIP_KEYS = new Set(['$$typeof', 'prototype', 'constructor', 'then']);
const plplSupabaseLazy =
  plplSupabaseUrl && plplSupabaseAnonKey
    ? new Proxy(
        {},
        {
          get(_, prop) {
            if (typeof prop === 'symbol' || PLPL_PROXY_SKIP_KEYS.has(prop)) {
              return undefined;
            }
            return getPlplSupabaseClient()[prop];
          },
        }
      )
    : null;

export const plplSupabase = plplSupabaseLazy;

// 모든 클라이언트를 객체로 묶어서 관리 (선택사항)
export const supabaseClients = {
  main: supabase,
  plpl: plplSupabaseLazy,
};

/**
 * 메인 Supabase 세션 및 토큰 삭제
 */
export const clearMainSession = async () => {
  try {
    // 세션 로그아웃
    await supabase.auth.signOut();
    
    // localStorage에서 토큰 삭제
    if (typeof window !== 'undefined') {
      localStorage.removeItem('sb-main-auth-token');
      localStorage.removeItem('impersonateEmail'); // impersonateEmail도 삭제
      // Supabase가 사용하는 다른 키들도 삭제
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-main-')) {
          localStorage.removeItem(key);
        }
      });
      
      // sessionStorage도 확인
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('sb-main-')) {
          sessionStorage.removeItem(key);
        }
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('메인 세션 삭제 중 오류:', error);
    return { success: false, error };
  }
};

/**
 * PLPL Supabase 세션 및 토큰 삭제
 */
export const clearPlplSession = async () => {
  try {
    // 세션 로그아웃
    if (plplSupabase) {
      await plplSupabase.auth.signOut();
    }
    
    // localStorage에서 토큰 삭제
    if (typeof window !== 'undefined') {
      localStorage.removeItem('sb-plpl-auth-token');
      localStorage.removeItem('impersonateEmail'); // impersonateEmail도 삭제
      // Supabase가 사용하는 다른 키들도 삭제
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-plpl-')) {
          localStorage.removeItem(key);
        }
      });
      
      // sessionStorage도 확인
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('sb-plpl-')) {
          sessionStorage.removeItem(key);
        }
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('PLPL 세션 삭제 중 오류:', error);
    return { success: false, error };
  }
};

/**
 * 모든 Supabase 세션 및 토큰 삭제
 */
export const clearAllSessions = async () => {
  try {
    await Promise.all([
      clearMainSession(),
      clearPlplSession()
    ]);
    
    return { success: true };
  } catch (error) {
    console.error('모든 세션 삭제 중 오류:', error);
    return { success: false, error };
  }
};

/**
 * localStorage에서 모든 Supabase 관련 항목 삭제 (강제 삭제)
 */
export const forceClearAllSupabaseStorage = () => {
  if (typeof window === 'undefined') return;
  
  try {
    // localStorage에서 모든 Supabase 관련 키 삭제
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) {
        localStorage.removeItem(key);
      }
    });
    localStorage.removeItem('impersonateEmail'); // impersonateEmail도 삭제
    localStorage.removeItem('impersonateRecent'); // 운영 서랍 최근 본 회원(2026-10-20)도 함께
    
    // sessionStorage도 확인
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
    
    console.log('모든 Supabase 저장소 항목이 삭제되었습니다.');
    return { success: true };
  } catch (error) {
    console.error('저장소 삭제 중 오류:', error);
    return { success: false, error };
  }
};

/**
 * 인증 에러 발생 시 토큰을 자동으로 삭제하는 헬퍼 함수
 * @param {Error} error - 발생한 에러 객체
 * @param {string} clientType - 'main' 또는 'plpl' (기본값: 'main')
 */
export const handleAuthError = async (error, clientType = 'main') => {
  // 인증 관련 에러 코드들
  const authErrorCodes = [
    'invalid_token',
    'token_expired',
    'invalid_credentials',
    'invalid_session',
    'session_not_found',
    'unauthorized',
    'invalid_grant',
    'invalid_request',
    'JWTExpired',
    'JWTInvalid'
  ];
  
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorCode = error?.code?.toLowerCase() || '';
  
  // 인증 에러인지 확인
  const isAuthError = authErrorCodes.some(code => 
    errorMessage.includes(code) || errorCode.includes(code)
  ) || error?.status === 401 || error?.status === 403;
  
  if (isAuthError) {
    console.warn('인증 에러 감지, 토큰 삭제 중...', error);
    
    try {
      if (clientType === 'plpl') {
        await clearPlplSession();
      } else {
        await clearMainSession();
      }
      
      // 모든 세션도 삭제 (안전을 위해)
      await clearAllSessions();
      
      return { cleared: true, error };
    } catch (clearError) {
      console.error('토큰 삭제 중 오류:', clearError);
      // 강제 삭제 시도
      forceClearAllSupabaseStorage();
      return { cleared: true, error, clearError };
    }
  }
  
  return { cleared: false, error };
};
